import {
	Directive, ElementRef, HostListener, OnInit, OnDestroy
} from '@angular/core';

import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { Subscription, delay, filter, firstValueFrom, map, of } from 'rxjs';
import { focusNextTarget } from 'rev-shared/util/FocusHelper';

/**
 * Override for ngx-bootstrap to add better keyboard controls.
 * Adds support for container=body.
 * Escape key closes menu if a menu item is focused
 * Wrapping from bottom to top
 * handle focusin to keep focused item in sync with internal state
 */
@Directive({
	selector: '[vbDropdown]',
	standalone: false
})
export class VbDropdownDirective implements OnInit, OnDestroy {

	private menuElement: HTMLElement;
	private sub: Subscription;
	private isKeyboardControlled = false;
	private isTabPressed = false;

	private get toggleBtn(): HTMLElement {
		return this.elementRef.nativeElement.querySelector('[dropdownToggle]');
	}

	constructor(
		private readonly dropdown: BsDropdownDirective,
		private readonly elementRef: ElementRef
	) {
		//Override arrowdown/arrowup hostbinding method.
		dropdown.navigationClick = (event: KeyboardEvent)=> { };
	}

	public ngOnInit(): void {
		this.sub = this.dropdown.isOpenChange.pipe(
			filter(Boolean),
		).subscribe(() => this.handleMenuOpen(true));
	}

	public ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	private handleMenuOpen(retry: boolean = false): void {
		this.updateMenuEl();
		if(!this.menuElement) {
			if(retry) {
				//In the inline case, menu element is null first time
				setTimeout(() => this.handleMenuOpen(), 50);
			}
			return;
		}

		if(!this.isKeyboardControlled) {
			return;
		}

		const focusItem = this.queryMenuItem();
		if(focusItem) {
			focusItem.focus();
		}
		else {
			setTimeout(() => {
				const focusItem = this.queryMenuItem();
				if(focusItem) {
					focusItem.focus();
				}
				else {
					console.error('unable to autofocus menu item');

				}
			}, 100);
		}
	}

	private queryMenuItem(): HTMLElement {
		return Array.from<HTMLElement>(this.menuElement.querySelectorAll('button, a, input, textarea, select, [tabindex="0"]'))
			.find(e => e.checkVisibility() && !e.hasAttribute('disabled'));
	}

	private moveFocus(backwards: boolean): void {
		const menu = this.menuElement;
		//Loop between menu items and toggle button
		const targets = [
			this.toggleBtn,
			...Array.from(menu.querySelectorAll<HTMLElement>('.dropdown-item:not([disabled])'))
				.filter(el => el.checkVisibility())
		];
		focusNextTarget(targets, backwards);
	}

	@HostListener('keydown', ['$event'])
	public onKeydown(event: KeyboardEvent): void {
		switch(event.code) {
			case 'Tab':
				this.isTabPressed = true;
				break;
			case 'Enter':
			case 'Space':
				this.isKeyboardControlled = true;
				break;
			case 'ArrowDown':
			case 'ArrowRight':
				if(this.dropdown.isOpen) {
					this.moveFocus(false);
					event.preventDefault();
				}
				break;
			case 'ArrowUp':
			case 'ArrowLeft':
				if(this.dropdown.isOpen) {
					this.moveFocus(true);
					event.preventDefault();
				}
				break;
		}
	}

	@HostListener('keyup', ['$event'])
	public onKeyup(event) {
		if (event.code === 'Tab') {
			this.isTabPressed = false;
		}
		else if (event.code === 'Enter' || event.code === 'Space') {
			setTimeout(() => this.isKeyboardControlled = false, 100);
		}
	}

	private onFocusOut($event) {
		if ($event.relatedTarget !== null && !this.menuElement.contains($event.relatedTarget) && this.isTabPressed) {
			this.dropdown.hide();
		}
	};

	private updateMenuEl(): void {
		const dd = this.dropdown as any;
		const menuEl = this.dropdown.container === 'body' ?
			dd._dropdown?.instance?._element?.nativeElement :
			dd._inlinedMenu?.rootNodes[0];
		//if using body container, the menu will be recreated each time its opened
		if(!menuEl || this.menuElement === menuEl) {
			return;
		}
		this.menuElement = menuEl;

		this.menuElement.addEventListener('focusout', e => this.onFocusOut(e));

		this.menuElement.addEventListener('keydown', e => {
			switch(e.code) {
				case 'Escape':
					this.dropdown.hide();
					this.toggleBtn?.focus();
					break;
				case 'ArrowDown':
				case 'ArrowRight':
					this.moveFocus(false);
					break;
				case 'ArrowUp':
				case 'ArrowLeft':
					this.moveFocus(true);
					break;
				default:
					return;
			}
			e.stopPropagation();
			e.preventDefault();
		});
	}
}
